import React, { useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { Save, X } from "lucide-react";
import ErrorNotification from "../../ErrorNotification";

const CommonConfigTable = (Props) => {
  const data = Props.data || [];
  const [isAdding, setIsAdding] = useState(false);
  const [newRow, setNewRow] = useState({ name: "", description: "" });
  const [openDialog, setOpenDialog] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0 });
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const displayData = showAll ? data : data.slice(0, 4);

  let accessToken = useSelector((state) => state.accessToken.value);
  const apiUrl = process.env.REACT_APP_API_URL;

  const handleSubmit = async () => {
    const query = `
      mutation MyMutation($id: String!, $key:String!, $value:String!) {
        add${Props.name}(id: $id, key:$key , value:$value ) {
          message
        }
      }
    `;
    const variables = {
      id: "1",
      key: newRow.name,
      value: newRow.description,
    };

    await axios
      .post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      )
      .then((response) => {
        if (
          response.data.errors &&
          response.data.errors[0].errorType == "Unauthorized"
        )
          setError(
            "Edit access is not granted to your account for this resource."
          );
      })
      .catch((error) => {
        console.log(error);
      });
    Props.setRefresh(!Props.refresh);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRow({ ...newRow, [name]: value });
  };

  const handleAddRow = (e) => {
    e.preventDefault();
    handleSubmit();
    setNewRow({ name: "", description: "" });
    setIsAdding(false);
  };

  const handleDeleteConfirmation = (recordName) => {
    setRecordToDelete(recordName);
    setOpenDialog(true);
  };

  const deleteRecord = async () => {
    setIsSubmitting(true);
    const query = `
      mutation MyMutation($id:String!, $key:String!) {
        delete${Props.name}(id: $id, key: $key) {
          message
        }
      }
    `;

    const variables = {
      id: "1",
      key: recordToDelete,
    };

    await axios
      .post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      )
      .then((response) => {
        if (
          response.data.errors &&
          response.data.errors[0].errorType == "Unauthorized"
        )
          setError(
            "Delete access is not granted to your account for this resource."
          );
      })
      .catch((error) => {
        console.log(error);
      });
    setOpenDialog(false);
    setRecordToDelete(null);
    Props.setRefresh(!Props.refresh);
    setIsSubmitting(false);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg border border-gray-200">
        {error && (
          <ErrorNotification message={error} onClose={() => setError(null)} />
        )}
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-[#003366]">{Props.name}</h3>
            <button
              onClick={() => setIsAdding(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add {Props.name}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#003366]/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isAdding && (
                  <tr className="bg-blue-50">
                    <td className="px-6 py-4">
                      <input
                        required
                        type="text"
                        name="name"
                        value={newRow.name}
                        onChange={handleInputChange}
                        placeholder="Enter name"
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003366]"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        required
                        type="text"
                        name="description"
                        value={newRow.description}
                        onChange={handleInputChange}
                        placeholder="Enter description"
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003366]"
                      />
                    </td>
                    <td className="px-6 py-4 text-center space-x-2">
                      <button
                        onClick={handleAddRow}
                        disabled={newRow.name == "" || newRow.description == ""}
                        className={
                          newRow.name == "" || newRow.description == ""
                            ? "text-gray-600"
                            : `text-green-600 hover:text-green-800 font-medium`
                        }
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setNewRow({ name: "", description: "" });
                          setIsAdding(false);
                        }}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                )}
                {displayData.map((row, i) => (
                  <tr key={i} className="hover:bg-[#003366]/5">
                    <td className="px-6 py-4 whitespace-nowrap text-[#003366]">
                      {row.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {row.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleDeleteConfirmation(row.name)}
                        className="text-red-600 hover:text-red-800"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            visible: true,
                            x: rect.left + rect.width / 2,
                            y: rect.bottom + 5,
                          });
                        }}
                        onMouseLeave={() =>
                          setTooltip({ visible: false, x: 0, y: 0 })
                        }
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.length > 4 && (
              <div className="text-center mt-4">
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-[#003366] hover:text-[#004488] font-medium"
                >
                  {showAll ? "Show Less" : "Show More"}
                </button>
              </div>
            )}
            {data.length === 0 && !isAdding && (
              <div className="text-center py-12">
                <div className="text-[#003366]">
                  {Props.loading ? (
                    <div className="flex justify-center items-center p-8">
                      <CircularProgress size={40} style={{ color: "#003366" }} />
                    </div>
                  ) : (
                    <>
                      <svg
                        className="w-12 h-12 mx-auto mb-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 12H4"
                        />
                      </svg>
                      <p className="text-lg font-semibold">No Data Available</p>
                      <p className="mt-2">
                        Click the Add New button to add your first item
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {tooltip.visible && (
        <div
          style={{
            position: "fixed",
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translateX(-50%)",
            zIndex: 1000,
          }}
          className="bg-black text-white text-sm rounded px-2 py-1"
        >
          Delete
        </div>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            borderRadius: "8px",
            padding: "8px",
            maxWidth: "450px",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          style={{
            padding: "16px 24px",
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#003366",
          }}
        >
          {"Confirm Deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            style={{
              color: "#4a4a4a",
              fontSize: "1rem",
              marginBottom: "8px",
            }}
          >
            Are you sure you want to delete the record <b>{recordToDelete}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px", gap: "12px" }}>
          <button
            onClick={() => setOpenDialog(false)}
            // className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-[#003366] rounded hover:bg-[#FFD700]/80"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#FFD700",
              color: "#003366",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#FFE44D")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#FFD700")
            }
          >
            {/* <X className="w-4 h-4" /> */}
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={deleteRecord}
            // className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#003366",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#004488")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#003366")
            }
            autoFocus
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Save size={16} />
            )}
            Confirm
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommonConfigTable;
